// src/components/SplashScreen/SplashScreen.jsx
import { useEffect, useMemo, useState } from 'react';
import styles from './SplashScreen.module.css';
import logo from '../../assets/new-logo.png';

const LOADING_STEPS = [
  { progress: 18, text: 'Warming up the engines', delay: 280 },
  { progress: 38, text: 'Loading your workspace', delay: 720 },
  { progress: 58, text: 'Syncing travel data', delay: 1150 },
  { progress: 78, text: 'Configuring modules', delay: 1600 },
  { progress: 92, text: 'Polishing the details', delay: 2000 },
  { progress: 100, text: 'Welcome to Sunsky', delay: 2350 },
];

const WORDMARK = ['S', 'u', 'n', 's', 'k', 'y'];

const SplashScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Initializing');
  const [fadeOut, setFadeOut] = useState(false);

  // Stable random fields so they don't reshuffle on every state change.
  const stars = useMemo(
    () =>
      Array.from({ length: 46 }, () => ({
        left: Math.random() * 100,
        top: Math.random() * 62,
        size: 1 + Math.random() * 2.2,
        delay: Math.random() * 3,
        dur: 2.4 + Math.random() * 2.6,
      })),
    []
  );

  const embers = useMemo(
    () =>
      Array.from({ length: 18 }, () => ({
        left: 4 + Math.random() * 92,
        size: 3 + Math.random() * 6,
        delay: Math.random() * 5,
        dur: 5 + Math.random() * 4,
        drift: -30 + Math.random() * 60,
        opacity: 0.25 + Math.random() * 0.45,
      })),
    []
  );

  useEffect(() => {
    const timers = LOADING_STEPS.map(({ progress, text, delay }) =>
      setTimeout(() => {
        setProgress(progress);
        setLoadingText(text);
      }, delay)
    );

    const fadeTimer = setTimeout(() => setFadeOut(true), 2750);
    const completeTimer = setTimeout(() => onComplete?.(), 3300);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  return (
    <div className={`${styles.splash} ${fadeOut ? styles.fadeOut : ''}`}>
      {/* Layered dawn sky */}
      <div className={styles.sky} />
      <div className={styles.aurora} />
      <div className={styles.sunGlow} />
      <div className={styles.vignette} />

      {/* Twinkling stars */}
      <div className={styles.stars} aria-hidden="true">
        {stars.map((s, i) => (
          <span
            key={i}
            className={styles.star}
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.dur}s`,
            }}
          />
        ))}
      </div>

      <div className={styles.content}>
        {/* Logo medallion */}
        <div className={styles.stage}>
          <div className={styles.halo} />
          <div className={styles.ring} />
          <div className={styles.ringInner} />
          <div className={styles.medallion}>
            <span className={styles.sheen} />
            <img src={logo} alt="Sunsky" className={styles.logo} />
          </div>
        </div>

        {/* Brand */}
        <div className={styles.brand}>
          <h1 className={styles.wordmark} aria-label="Sunsky">
            {WORDMARK.map((ch, i) => (
              <span
                key={i}
                className={styles.letter}
                style={{ animationDelay: `${0.5 + i * 0.08}s` }}
              >
                {ch}
              </span>
            ))}
          </h1>
          <p className={styles.tagline}>Travel Management Platform</p>
        </div>

        {/* Progress */}
        <div className={styles.loader}>
          <div className={styles.track}>
            <div className={styles.fill} style={{ width: `${progress}%` }}>
              <span className={styles.shimmer} />
            </div>
          </div>
          <div className={styles.meta}>
            <span className={styles.status}>{loadingText}</span>
            <span className={styles.percent}>{progress}%</span>
          </div>
        </div>
      </div>

      {/* Rising warm embers */}
      <div className={styles.embers} aria-hidden="true">
        {embers.map((e, i) => (
          <span
            key={i}
            className={styles.ember}
            style={{
              left: `${e.left}%`,
              width: `${e.size}px`,
              height: `${e.size}px`,
              opacity: e.opacity,
              '--drift': `${e.drift}px`,
              animationDelay: `${e.delay}s`,
              animationDuration: `${e.dur}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default SplashScreen;
